import GridActions from '@/components/Base/GridActions/actions'
import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { AttributionBySectorRootComponent } from '@/components/ReturnVariance/AttributionBySector/AttributionBySectorRootComponent'
import { ReturnVarianceReportOverview } from '@/components/ReturnVariance/ReportOverview/ReportOverview'
import { RVDashboardNavigation } from '@/components/ReturnVariance/RVDashboardNavigation'
import { RVSummaryChart } from '@/components/ReturnVariance/RVSummaryChart'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { IReturnVarianceReportDailyAttribution } from '@/interfaces/rvReport.interface'
import { ReportDashboardLayout } from '@/Layouts/ReportDashboardLayout'
import {
  getColumnDefByReportType,
  getReturnVarianceReportDailyAttribution,
} from '@/services/axiomReport.service'
import { AgGridReact } from '@ag-grid-community/react'
import {
  CloseFullscreen,
  InfoRounded,
  MoreVert,
  OpenInFull,
} from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const RVDashboardPage: React.FC = () => {
  const navigate = useNavigate()

  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const [showLoader, setShowLoader] = useState(false)
  const [rowData, setRowData] = useState<
    IReturnVarianceReportDailyAttribution[]
  >([])
  const [gridReference, setGridReference] = useState<AgGridReact>()
  const [isAttributionBySectorExpanded, setAttributionBySectorExpanded] =
    useState(false)

  const fetchRvDailyAttribution = async () => {
    try {
      setShowLoader(true)
      const res = await getReturnVarianceReportDailyAttribution(
        selectedReport.reportId,
      )
      if (res) {
        setRowData(res)
      }
    } catch (error) {
    } finally {
      setShowLoader(false)
    }
  }

  const fetchColumnsHeaderMetaData = async () => {
    try {
      const columnsMetaData = await getColumnDefByReportType(AxiomReportType.RV)
      sessionStorage.setItem(
        AxiomReportType.RV,
        JSON.stringify(columnsMetaData),
      )
      fetchRvDailyAttribution()
    } catch (err) {}
  }

  useEffect(() => {
    const isStoredInSession = sessionStorage.getItem(AxiomReportType.RV)
    if (!isStoredInSession) {
      fetchColumnsHeaderMetaData()
    } else {
      fetchRvDailyAttribution()
    }
  }, [selectedReport])

  // Daily Contribution Menu
  const [dailyContributionMenuEl, setDailyContributionMenuEl] =
    React.useState<null | HTMLElement>(null)
  const displayDailyContributionMenu = Boolean(dailyContributionMenuEl)
  const openDailyContributionMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setDailyContributionMenuEl(event.currentTarget)
  }
  const closeDailyContributionMenu = () => {
    setDailyContributionMenuEl(null)
  }

  return (
    <ReportDashboardLayout
      navigation={<RVDashboardNavigation />}
      selectedReport={selectedReport}
    >
      <Box
        display='grid'
        gridTemplateRows={isAttributionBySectorExpanded ? '0 1fr' : 'auto auto'}
        gap={isAttributionBySectorExpanded ? 0 : 4}
        boxSizing='border-box'
      >
        {/* TOP SECTION */}
        <Box
          display='grid'
          gridTemplateColumns='auto 1fr'
          gap={4}
          boxSizing='border-box'
          height={isAttributionBySectorExpanded ? '0px' : 'auto'}
          visibility={isAttributionBySectorExpanded ? 'hidden' : 'initial'}
        >
          {/* Data POints */}
          <ReturnVarianceReportOverview
            reportId={selectedReport.reportId}
            reportType={selectedReport.reportType}
          />
          {/* GFPA Portfolio Overview / Summary Chart */}
          <Card sx={{ boxSizing: 'border-box' }}>
            <CardHeader
              title={<Typography variant='h3'>Daily Attribution</Typography>}
              action={
                <>
                  <IconButton
                    onClick={openDailyContributionMenu}
                    aria-controls={
                      displayDailyContributionMenu
                        ? 'daily-contribution-menu'
                        : undefined
                    }
                  >
                    <MoreVert fontSize='inherit' />
                  </IconButton>
                  <Menu
                    id='basic-menu'
                    anchorEl={dailyContributionMenuEl}
                    open={displayDailyContributionMenu}
                    onClose={closeDailyContributionMenu}
                    MenuListProps={{
                      'aria-labelledby': 'daily-contribution-menu',
                      style: { minWidth: '15rem' },
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem
                      onClick={() =>
                        navigate(
                          `/reports/${selectedReport.reportId}/rv/summary`,
                        )
                      }
                    >
                      View Details
                    </MenuItem>
                  </Menu>
                </>
              }
            />
            <CardContent
              sx={{
                boxSizing: 'border-box',
                height: 'calc(100% - 4rem)',
              }}
            >
              {showLoader ? (
                <LoadingCard />
              ) : rowData.length ? (
                <RVSummaryChart chartData={rowData} />
              ) : (
                <NoDataToDisplayCard />
              )}
            </CardContent>
          </Card>
        </Box>
        {/* BOTTOM SECTION */}
        <Card
          sx={{
            boxSizing: 'border-box',
            height: isAttributionBySectorExpanded
              ? document.body.clientHeight - 280
              : 'auto',
            overflow: isAttributionBySectorExpanded ? 'auto' : 'inherit',
            zIndex: isAttributionBySectorExpanded ? 9 : 'inherit',
          }}
        >
          <CardHeader
            title={
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
                gap={4}
              >
                <Typography variant='h3'>Attribution by Group</Typography>
                <Tooltip title='Attribution by Group' placement='right'>
                  <IconButton
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                  >
                    <InfoRounded fontSize='small' color='info' />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
            titleTypographyProps={{
              component: 'div',
              display: 'block',
            }}
            action={
              <Stack direction='row'>
                <GridActions gridApiRef={gridReference} />
                <IconButton
                  size='small'
                  disableRipple
                  onClick={() =>
                    setAttributionBySectorExpanded((expanded) => !expanded)
                  }
                >
                  {isAttributionBySectorExpanded ? (
                    <CloseFullscreen fontSize='inherit' />
                  ) : (
                    <OpenInFull fontSize='inherit' />
                  )}
                </IconButton>
              </Stack>
            }
            sx={{
              pb: 0,
            }}
          />
          <CardContent
            sx={{
              boxSizing: 'border-box',
              pt: 0,
            }}
          >
            <AttributionBySectorRootComponent
              setGridReference={setGridReference}
            />
          </CardContent>
        </Card>
      </Box>
    </ReportDashboardLayout>
  )
}
